import { judge_value_valid } from '@/utils/chart/verification.js'
import { convert_ISO_datetime_str, get_datetime_obj, transform_datetime_str_to_chart_need_format } from '@/utils/common/custom_time.js'

import * as d3 from 'd3'

export const get_content_instance = (id) => {
    return d3.select(`#${id}`)
        .append('div')
        .style('opacity', 0)
        .attr('class', 'tooltip')
}

const get_vertical_line_instance = (svg, height) => {
    return svg.append('rect')
        .attr('id', 'tooltip_display_line')
        .attr('fill', '#191f35')
        .attr('width', 1)
        .attr('height', height)
        .style('opacity', 0)
}

const get_highlight_circle_instance = (svg) => {
    return svg.append('circle')
        .attr('id', 'tooltip_highlight_circle')
        .style('stroke-width', 2)
        .style('stroke', '#191f35')
        .attr('fill', 'white')
        .attr('r', 6)
        .style('opacity', 0)
}

export const draw_tooltip = ({ basic, tooltip, data, extra_data = null }) => {
    const {
        svg,
        id,
        scale_time,
        scale_data: from_parent_scale_function,
        scale_y,
        width,
        height,
        margin,
    } = basic

    const { 
        text: tooltip_text,
        unit: tooltip_unit,
        gust_text,
        no_data_text,
    } = tooltip

    const {
        content: content_data,
        gust_wind_data,
    } = data

    const input_data = gust_wind_data ? reconstruct_include_gust_tooltip_data(data) : reconstruct_tooltip_data(data)
    const scale_function = gust_wind_data ? create_tooltip_scale_function(input_data, width) : from_parent_scale_function
    const extra_input_data = extra_data ? reconstruct_tooltip_data({x_axis: data.x_axis, content: extra_data.content}) : []

    const content = get_content_instance(id)
    const vertical_line = get_vertical_line_instance(svg, height)
    const highlight_circle = get_highlight_circle_instance(svg)
    
    const data_length = content_data.length
    const is_value_at_left_side = (i) => data_length / 2 > i

    const time_parse = d3.timeParse('%Y-%m-%d %H:%M')

    svg.selectAll('tooltip_rect')
        .data(input_data)
        .enter()
        .append('rect')
        .attr('width', scale_function(1))
        .attr('height', height)
        .attr('x', (d) => scale_time(time_parse(d.time)))
        .style('opacity', 0)
        .style('transform', `translateX(${margin.left}px)`)
        .on('mouseover', (d, i) => {      
            const scale_time_position = scale_time(time_parse(d.time))
            const value = d.value
            vertical_line
                .attr('x', scale_time_position)
                .style('transform', `translateX(${margin.left}px)`)
                .style('opacity', 1)
                .raise()

            highlight_circle
                .attr('cx', margin.left + scale_time_position)
                .attr('cy', judge_value_valid(value) ? scale_y(value) : height)
                .style('opacity', 1)
                .raise()

            content
                .transition()
                .duration(200)
                .style('opacity', 1)
                
            content
                .html(() => {
                    const detail_value = judge_value_valid(value) ? `${parseFloat(value)} ${tooltip_unit}` : no_data_text
                    const datetime_value = d.time
                    const gust_value = d.gust_value
                    const detail_gust_value = judge_value_valid(gust_value) ? `${parseFloat(gust_value)} ${tooltip_unit}` : ''

                    let html_content = `
                        ${datetime_value}</br>
                        ${tooltip_text}: ${detail_value}</br>`
                    if(extra_data) {
                        const extra_value = extra_input_data[i].value
                        const extra_detail_value = judge_value_valid(extra_value) ? `${parseFloat(extra_value)} ${extra_data.unit}` : no_data_text
                        html_content += extra_data ? `${extra_data.text}: ${extra_detail_value}</br>` :''
                    }
                    html_content += `${ detail_gust_value ? '</br>' + gust_text + ':' + detail_gust_value : '' }`
                    return html_content
                })
                .style('left', () => {
                    const offset = is_value_at_left_side(i) ? 48 : 32
                    return `${margin.left + scale_time_position + offset}px`
                } )
                .style('top', judge_value_valid(value) ? `${margin.top + scale_y(value)}px` : `${margin.top + height}px`)
                .style('transform', () => {
                    const translate_x_value = is_value_at_left_side(i) ? '0' : '-100%'
                    return `translate(${translate_x_value}, -100%)`
                })
        })
        .on('mouseout', () => {
            vertical_line.style('opacity', 0);
            highlight_circle.style('opacity', 0);
            content.transition().style('opacity', 0);
        })
}

const reconstruct_include_gust_tooltip_data = ({
    content: content_data,
    x_axis: x_axis_data,
    gust_wind_data,
    gust_time_series_data,
}) => {
    const gust_tooltip_data = [... new Set([...x_axis_data, ...gust_time_series_data])]
        .map(datum => new Date(convert_ISO_datetime_str(datum)))
        .filter(d => d instanceof Date && !isNaN(d))
        .sort((a, b) => a-b)
        .map(time => {
            const { yyyy, mm, dd, HH, MM } = get_datetime_obj(time)
            const chart_time = `${yyyy}-${mm}-${dd} ${HH}:${MM}`
            const for_indexOf_time = `${yyyy}-${mm}-${dd} ${HH}:${MM}:00`
            const wind_index = x_axis_data.indexOf(for_indexOf_time)
            const gust_index = gust_time_series_data.indexOf(for_indexOf_time)
            const wind_value = content_data[wind_index]
            const gust_value = gust_wind_data[gust_index]

            return {
                value: wind_value,
                gust_value,
                time: chart_time
            }
        })
    
    return gust_tooltip_data
}

const reconstruct_tooltip_data = ({content: content_data, x_axis: x_axis_data}) => {
    return x_axis_data.map((time, index) => {
        return {
            value: content_data[index],
            time: transform_datetime_str_to_chart_need_format(time),
        }
    })
}

const create_tooltip_scale_function = (input_data, width) => {
    return d3.scaleLinear().domain([0, input_data.length-1]).range([0, width])
}